/**
 * Quiz Timer Component
 *
 * Displays countdown timer for timed quizzes and auto-submits when time expires.
 */

import { useEffect, useState, useCallback } from 'react';

interface QuizTimerProps {
  timeLimitMinutes: number;
  onTimeUp: () => void;
  paused?: boolean;
}

export function QuizTimer({ timeLimitMinutes, onTimeUp, paused = false }: QuizTimerProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(timeLimitMinutes * 60);
  const [hasTriggered, setHasTriggered] = useState(false);

  const handleTimeUp = useCallback(() => {
    if (!hasTriggered) {
      setHasTriggered(true);
      onTimeUp();
    }
  }, [onTimeUp, hasTriggered]);

  useEffect(() => {
    if (paused || remainingSeconds <= 0) return;

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(interval);
          handleTimeUp();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [paused, handleTimeUp, remainingSeconds]);

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const isLowTime = remainingSeconds <= 60; // Last minute
  const isCriticalTime = remainingSeconds <= 30; // Last 30 seconds

  return (
    <div
      className={`quiz-timer fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 ${
        isCriticalTime
          ? 'bg-red-600 text-white animate-pulse'
          : isLowTime
          ? 'bg-yellow-500 text-white'
          : 'bg-gray-800 text-white'
      }`}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className="font-mono text-lg font-bold">
        {formatTime(remainingSeconds)}
      </span>
      {paused && <span className="text-xs">(Paused)</span>}
    </div>
  );
}

export default QuizTimer;
